import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import { Stack, Typography } from "@mui/material";
import type { SupportReportType } from "Components/Support/SupportReportType";
import { SupportReportTypeCard } from "Components/Support/SupportReportTypeCard";
import { Resources, useResource } from "Translations/Resources";
import type { ReactNode } from "react";

const RESOURCES = Resources.Support.Choice;

type Option = {
  reportType: SupportReportType;
  icon: ReactNode;
  title: string;
  description: string;
  isAvailable: boolean;
  unavailableNote?: string;
};

type Props = {
  onTypeSelected: (reportType: SupportReportType) => void;
  isTechnicalProblemAvailable: boolean;
};

export const SupportReportTypePicker = ({
  onTypeSelected,
  isTechnicalProblemAvailable,
}: Props) => {
  const { t } = useResource();

  const options: ReadonlyArray<Option> = [
    {
      reportType: "technicalProblem",
      icon: <BugReportRoundedIcon />,
      title: t(RESOURCES.TechnicalProblemButton),
      description: t(RESOURCES.TechnicalProblemDescription),
      isAvailable: isTechnicalProblemAvailable,
      unavailableNote: t(RESOURCES.TechnicalProblemUnavailable),
    },
    {
      reportType: "supportRequest",
      icon: <SupportAgentRoundedIcon />,
      title: t(RESOURCES.SupportRequestButton),
      description: t(RESOURCES.SupportRequestDescription),
      isAvailable: true,
    },
    {
      reportType: "contentReport",
      icon: <ReportProblemIcon />,
      title: t(Resources.Support.ContentReport.PickerButton),
      description: t(Resources.Support.ContentReport.PickerDescription),
      isAvailable: true,
    },
  ];

  return (
    <Stack gap={3}>
      <Typography color="text.secondary">{t(RESOURCES.Description)}</Typography>
      <Stack gap={2}>
        {options.map(
          ({
            reportType,
            icon,
            title,
            description,
            isAvailable,
            unavailableNote,
          }) => (
            <Stack key={reportType} gap={1}>
              <SupportReportTypeCard
                icon={icon}
                title={title}
                description={description}
                isDisabled={!isAvailable}
                onClick={() => onTypeSelected(reportType)}
              />
              {!isAvailable && !!unavailableNote && (
                <Typography variant="body2" color="text.secondary">
                  {unavailableNote}
                </Typography>
              )}
            </Stack>
          ),
        )}
      </Stack>
    </Stack>
  );
};
